import { useQuery } from '@tanstack/vue-query';
import { request } from '@/api/request';
import { ApiError, type ApiTypedError } from '@/api/errors';
import { mergeQueryOptions, type QueryOptions } from '@/modules/query/utils';
import type { ModificationType } from '@@/packages/shared/types/models/modification';
import type { OrganisationOwnershipStatus } from '@@/packages/shared/types/models/organisation';
import type { SupplierListReviewState } from '@@/packages/shared/types/models/connectionReview';
import type { ControlComplianceV2 } from './types';
import type { SupplierOverrideView } from './supplier';
import { CLIENT_INFO_QUERY, CLIENT_COMPLIANCE_VTWO_QUERY } from './queryKeys';

export type OrgInfo = {
  name: string;
  id: string;
  logoUrl: string;
  companyNumber: string;
  country: string;
  sector: string;
  tradingNames: string[];
  reviewState: SupplierListReviewState;
  ownershipStatus: OrganisationOwnershipStatus;
};

export type ClientInfoResponse = {
  connectionID: string;
  orgInfo: OrgInfo | null;
  initialAssessmentComplete: boolean;
  assessmentProgress: number;
  lastActiveAt: number | null;
};

export interface Answer {
  answer: string;
  answerID: string;
  controlID: string;
  productID?: string;
  answerSetID: string;
  documentIDs: string[];
  modifiedAt: string;
  notes: string;
  notApplicable: boolean;
  unconfirmedControlUpdate: boolean;
  archivedAt?: string;
  archivedBy?: string;
}

export const isClientOverrideView = (
  override: ClientOverrideView | SupplierOverrideView,
): override is ClientOverrideView => 'reason' in override && 'createdBy' in override;

export type ClientOverrideView = {
  id: string;
  controlID: string;
  connectionID: string;
  productID: string | null;
  type: ModificationType;
  reason: string;
  createdAt: string;
  createdBy: string | null;
};

type ByDomain = {
  percentageCompliant: number;
  numberNoncompliant: number;
};

export type GetClientComplianceResponseV2 = {
  compliancePercentage: number | null;
  complianceByDomain: Record<string, ByDomain>;
  controlCompliances: ControlComplianceV2[];
};

export function useClientInfo(clientID: MaybeRefOrGetter<string>, options?: QueryOptions) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [CLIENT_INFO_QUERY, toRef(clientID)] as const,
      async queryFn({ queryKey }) {
        const res = await request<ClientInfoResponse>('GET', `/client/${queryKey[1]}`);
        return res.data;
      },
    }),
  );

  return {
    ...query,
    clientInfo: query.data,
    orgInfo: computed(() => query.data.value?.orgInfo),
    // True when the profile failed to load because the connection is not full
    // (403 + insufficient_connection_status reason code) — currently a downgrade
    // to basic. Lets the UI show a tailored, actionable message. See SPUD-911.
    isConnectionDowngraded: computed(
      () =>
        query.error.value instanceof ApiError &&
        (query.error.value as ApiTypedError).response?.data?.reasonCode ===
          'insufficient_connection_status',
    ),
  };
}

export function useClientCompliance(clientID: MaybeRefOrGetter<string>, options?: QueryOptions) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [CLIENT_COMPLIANCE_VTWO_QUERY, toRef(clientID)] as const,
      async queryFn({ queryKey }) {
        const res = await request<GetClientComplianceResponseV2>(
          'GET',
          `/clients/${queryKey[1]}/compliance/v2`,
        );
        return res.data;
      },
    }),
  );

  return {
    ...query,
    query,
    compliance: query.data,
  };
}
